import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Spin } from 'antd'

const HELCIM_PAY_SCRIPT = 'https://secure.helcim.app/helcim-pay/services/start.js'

declare global {
  interface Window {
    appendHelcimPayIframe?: (checkoutToken: string, options?: Record<string, unknown>) => void
    removeHelcimPayIframe?: () => void
  }
}

function loadHelcimPayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.appendHelcimPayIframe) {
      resolve()
      return
    }
    const existing = document.querySelector(`script[src="${HELCIM_PAY_SCRIPT}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load HelcimPay.js')))
      return
    }
    const script = document.createElement('script')
    script.src = HELCIM_PAY_SCRIPT
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load HelcimPay.js'))
    document.body.appendChild(script)
  })
}

export interface HelcimPayCheckoutProps {
  checkoutToken: string
  secretToken: string
  onValidate: (payload: {
    rawDataResponse: string
    hashFromClient?: string
    checkoutToken: string
    secretToken: string
  }) => Promise<void>
  onSuccess: () => void
  onError: (message: string) => void
  amountLabel?: string
}

export function HelcimPayCheckout({
  checkoutToken,
  secretToken,
  onValidate,
  onSuccess,
  onError,
  amountLabel,
}: HelcimPayCheckoutProps) {
  const [loading, setLoading] = useState(true)
  const [validating, setValidating] = useState(false)
  const startedRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const startCheckout = async () => {
      try {
        await loadHelcimPayScript()
        if (cancelled || startedRef.current) return
        startedRef.current = true
        window.appendHelcimPayIframe?.(checkoutToken, { allowExit: true })
        setLoading(false)
      } catch (err: any) {
        onError(err.message || 'Failed to load Helcim checkout')
        setLoading(false)
      }
    }

    void startCheckout()

    const handleMessage = async (event: MessageEvent) => {
      const data = event.data
      if (!data || typeof data !== 'object') return
      if (data.eventName !== 'helcim-pay-complete' && data.eventStatus !== 'SUCCESS') return

      const rawDataResponse =
        typeof data.eventMessage === 'string'
          ? data.eventMessage
          : JSON.stringify(data.eventMessage ?? data)
      const hashFromClient = data.hash || data.eventHash

      setValidating(true)
      try {
        await onValidate({
          rawDataResponse,
          hashFromClient,
          checkoutToken,
          secretToken,
        })
        window.removeHelcimPayIframe?.()
        onSuccess()
      } catch (err: any) {
        onError(err.message || 'Payment validation failed')
      } finally {
        setValidating(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      cancelled = true
      window.removeEventListener('message', handleMessage)
      window.removeHelcimPayIframe?.()
    }
  }, [checkoutToken, secretToken, onValidate, onSuccess, onError])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin size="large" />
        <p>Loading secure checkout…</p>
      </div>
    )
  }

  return (
    <div>
      {amountLabel && (
        <Alert message={amountLabel} type="info" showIcon style={{ marginBottom: 16 }} />
      )}
      {validating && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin /> Confirming payment…
        </div>
      )}
      <div id="helcim-pay-iframe-target" />
      <Button
        onClick={() => window.removeHelcimPayIframe?.()}
        style={{ marginTop: 16 }}
        disabled={validating}
      >
        Cancel
      </Button>
    </div>
  )
}
